import { useMemo, useState } from "react";
import { Plug, Home, Zap, Clock, Euro } from "lucide-react";
import { isEV } from "@/data/ev";

const HOME_POWERS = [
  { kw: 3.7, label: "Prise 3,7 kW" },
  { kw: 7.4, label: "Wallbox 7,4 kW" },
  { kw: 11, label: "Wallbox 11 kW" },
  { kw: 22, label: "Wallbox 22 kW" },
];

function fmtTime(hours) {
  if (!isFinite(hours) || hours <= 0) return "—";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
}

const money = (v) => (isFinite(v) ? `${v.toFixed(2).replace(".", ",")} €` : "—");

// Pactly Electric — home vs fast-charge time & cost estimator for a listing.
// Uses the seller/scraper EV block (battery kWh + max charge kW). Interactive:
// the buyer tunes their home power and the kWh price.
export default function EVChargeSimulator({ car }) {
  const ev = car?.ev || {};
  const battery = Number(ev.battery_kwh) || 0;
  const [homeKw, setHomeKw] = useState(7.4);
  const [homePrice, setHomePrice] = useState(0.35);
  const [fastPrice, setFastPrice] = useState(0.59);

  const fastKw = Number(ev.charge_kw) || 50;

  const calc = useMemo(() => {
    // Home: full 0→100% at the selected AC power.
    const homeHours = battery / homeKw;
    const homeCost = battery * homePrice;
    // Fast DC: realistic 10→80% window (70% of the pack), taper factor ~0.75.
    const fastKwh = battery * 0.7;
    const fastHours = fastKwh / (fastKw * 0.75);
    const fastCost = fastKwh * fastPrice;
    return { homeHours, homeCost, fastKwh, fastHours, fastCost };
  }, [battery, homeKw, homePrice, fastKw, fastPrice]);

  if (!car || !isEV(car.fuel) || !battery) return null;

  return (
    <section data-testid="ev-charge-sim" className="theme-electric force-dark relative mt-6 overflow-hidden rounded-3xl border border-[#39FF6A]/15 bg-[#0B132B] p-6">
      <div className="ev-aurora" aria-hidden style={{ inset: "auto -30% -70% auto" }} />
      <div className="relative z-10">
        <div className="mb-5 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#39FF6A]/12 text-[#39FF6A]"><Plug size={18} /></span>
          <div>
            <h2 className="font-display text-lg font-extrabold text-white ev-glow-text">Simulateur de recharge</h2>
            <p className="text-xs text-slate-400">Temps & coût estimés · batterie {battery} kWh</p>
          </div>
        </div>

        {/* Controls */}
        <div className="mb-5 grid gap-3 sm:grid-cols-3">
          <label className="ev-glass rounded-2xl p-3">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-400">Recharge domicile</span>
            <select data-testid="ev-sim-home-kw" value={homeKw} onChange={(e) => setHomeKw(Number(e.target.value))}
              className="h-9 w-full rounded-lg border border-white/10 bg-white/5 px-2 text-sm text-white outline-none focus:border-[#39FF6A]/60">
              {HOME_POWERS.map((p) => <option key={p.kw} value={p.kw} className="bg-[#0B132B]">{p.label}</option>)}
            </select>
          </label>
          <label className="ev-glass rounded-2xl p-3">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-400">Prix domicile (€/kWh)</span>
            <input data-testid="ev-sim-home-price" type="number" step="0.01" value={homePrice}
              onChange={(e) => setHomePrice(Number(e.target.value) || 0)}
              className="h-9 w-full rounded-lg border border-white/10 bg-white/5 px-2 text-sm text-white outline-none focus:border-[#39FF6A]/60" />
          </label>
          <label className="ev-glass rounded-2xl p-3">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-400">Prix borne rapide (€/kWh)</span>
            <input data-testid="ev-sim-fast-price" type="number" step="0.01" value={fastPrice}
              onChange={(e) => setFastPrice(Number(e.target.value) || 0)}
              className="h-9 w-full rounded-lg border border-white/10 bg-white/5 px-2 text-sm text-white outline-none focus:border-[#39FF6A]/60" />
          </label>
        </div>

        {/* Results */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="ev-glass ev-glass-active rounded-2xl p-5" data-testid="ev-sim-home">
            <div className="flex items-center gap-2 text-[#39FF6A]"><Home size={16} /><span className="text-xs font-bold uppercase tracking-wide text-slate-300">À domicile · {homeKw} kW</span></div>
            <div className="mt-3 flex items-center justify-between">
              <div><div className="flex items-center gap-1.5 text-slate-400"><Clock size={13} /><span className="text-xs">0 → 100%</span></div>
                <div className="ev-num text-2xl font-extrabold text-white">{fmtTime(calc.homeHours)}</div></div>
              <div className="text-right"><div className="flex items-center justify-end gap-1.5 text-slate-400"><Euro size={13} /><span className="text-xs">Charge complète</span></div>
                <div className="ev-num text-2xl font-extrabold text-white">{money(calc.homeCost)}</div></div>
            </div>
          </div>
          <div className="ev-glass ev-glass-active rounded-2xl p-5" data-testid="ev-sim-fast">
            <div className="flex items-center gap-2 text-[#39FF6A]"><Zap size={16} className="fill-[#39FF6A]" /><span className="text-xs font-bold uppercase tracking-wide text-slate-300">Borne rapide · {fastKw} kW</span></div>
            <div className="mt-3 flex items-center justify-between">
              <div><div className="flex items-center gap-1.5 text-slate-400"><Clock size={13} /><span className="text-xs">10 → 80%</span></div>
                <div className="ev-num text-2xl font-extrabold text-white">{fmtTime(calc.fastHours)}</div></div>
              <div className="text-right"><div className="flex items-center justify-end gap-1.5 text-slate-400"><Euro size={13} /><span className="text-xs">≈ {Math.round(calc.fastKwh)} kWh</span></div>
                <div className="ev-num text-2xl font-extrabold text-white">{money(calc.fastCost)}</div></div>
            </div>
          </div>
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-slate-500">Estimations indicatives (pertes de charge ~15% incluses côté AC ; palier de puissance moyen 75% côté borne rapide). Le temps réel dépend de la température et de la courbe de charge du véhicule.</p>
      </div>
    </section>
  );
}
